import { useEffect, useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import {
  ChartLineUp, ForkKnife, Scales, Sparkle, FileText, Lock, ShieldCheck,
  CheckCircle, ArrowRight, Buildings, ChefHat, Leaf, Calculator,
  CaretRight, Lightning, Star,
} from "@phosphor-icons/react";

const HERO_BG = "https://images.unsplash.com/photo-1547592180-85f173990554?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600";
const FEATURE_KITCHEN = "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200";

const FEATURES = [
  {
    icon: Calculator,
    title: "Präzise Nährwert-Berechnung",
    desc: "Vollständige EU-Deklaration nach VO 1169/2011 — Brennwert, Big-7, 14 Pflichtallergene und über 30 Mikronährstoffe. Auf Knopfdruck.",
  },
  {
    icon: Leaf,
    title: "Nutri-Score live berechnet",
    desc: "Automatische A–E Klassifizierung deiner Produkte. Sofort sichtbar im Builder, auf PDF, Excel und auf jedem Etikett.",
  },
  {
    icon: ForkKnife,
    title: "Rezepte & Sub-Rezepte",
    desc: "Beilagen, Soßen, Komponenten — als Bausteine wiederverwenden. Allergene und Nährwerte rechnen sich rekursiv durch alle Ebenen.",
  },
  {
    icon: Scales,
    title: "Flexible Einheiten",
    desc: "Gramm, Kilogramm, Liter, Milliliter, Stückzahl — mit Dichte- und Stückgewicht-Umrechnung. So wie du in der Küche denkst.",
  },
  {
    icon: FileText,
    title: "EU-Konforme Exports",
    desc: "PDF-Datenblätter, Excel-Tabellen, druckfertige Etiketten (100×55 mm). Mit deinem Logo, deinen Firmendaten — bereit fürs Audit.",
  },
  {
    icon: ShieldCheck,
    title: "Audit-Trail & Versionierung",
    desc: "Wer hat wann was geändert? Jedes Rezept hat eine Historie mit Diff-Vergleich, Wiederherstellung und Änderungsbegründung.",
  },
];

const HOW_IT_WORKS = [
  {
    n: "01",
    title: "Zutaten anlegen",
    desc: "Manuell erfassen oder aus 50+ Standard-Zutaten wählen. Allergene, Dichte, Alkoholgehalt — alles in einem Formular.",
  },
  {
    n: "02",
    title: "Rezept zusammenstellen",
    desc: "Mengen eingeben — die App berechnet Brennwert, Makros, Mikros, Allergene und Nutri-Score in Echtzeit. Auch mit Sub-Rezepten.",
  },
  {
    n: "03",
    title: "Exportieren & drucken",
    desc: "PDF fürs Audit, Excel für die Buchhaltung, Etiketten direkt für die Verpackung. Mit eigenem Logo, sofort einsatzbereit.",
  },
];

const TARGET_AUDIENCE = [
  { icon: ChefHat,    label: "Catering & Großküchen" },
  { icon: Buildings,  label: "Lebensmittelhersteller" },
  { icon: ForkKnife,  label: "Restaurants & Gastronomie" },
  { icon: Sparkle,    label: "Manufakturen & Hofläden" },
];

export default function Landing() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [_scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Eingeloggte Nutzer direkt ins Dashboard
  if (!loading && user) return <Navigate to="/app" replace />;

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* ----- Top Navigation ----- */}
      <header className="fixed top-0 inset-x-0 z-40 bg-white/85 backdrop-blur-md border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
            <div className="w-8 h-8 rounded-sm bg-[#002FA7] flex items-center justify-center">
              <ChartLineUp size={18} weight="bold" color="white" />
            </div>
            <span className="font-heading font-bold text-base tracking-tight">NutriCalc</span>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm text-zinc-600">
            <a href="#features" className="hover:text-zinc-900 transition-colors">Funktionen</a>
            <a href="#how" className="hover:text-zinc-900 transition-colors">So funktioniert's</a>
            <a href="#pricing" className="hover:text-zinc-900 transition-colors">Preise</a>
            <a href="#compliance" className="hover:text-zinc-900 transition-colors">Compliance</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" className="text-sm text-zinc-600 hover:text-zinc-900 px-3 py-1.5"
              data-testid="nav-login">Login</Link>
            <Button onClick={() => nav("/registrieren")} className="bg-[#002FA7] hover:bg-[#00248a] rounded-sm h-8 text-xs"
              data-testid="nav-cta">
              Kostenlos testen
            </Button>
          </div>
        </div>
      </header>

      {/* ----- Hero ----- */}
      <section className="relative pt-28 pb-20 sm:pt-36 sm:pb-28 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-white via-white/85 to-white" />
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#002FA7]/10 text-[#002FA7] text-[11px] uppercase tracking-widest rounded-full mb-5">
              <Lightning size={12} weight="fill" /> EU VO 1169/2011 · Made in Germany
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-zinc-900">
              Nährwerte.
              <br />
              <span className="text-[#002FA7]">Auf Knopfdruck.</span>
            </h1>
            <p className="mt-5 text-base sm:text-lg text-zinc-600 leading-relaxed max-w-xl">
              Die professionelle Software für <strong>Lebensmittelindustrie</strong> und <strong>Gastronomie</strong>.
              Berechne präzise Nährwerte, Allergene und den Nutri-Score deiner Rezepte —
              transparent, nachvollziehbar und schöner als jede Excel-Tabelle.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button onClick={() => nav("/registrieren")}
                className="bg-[#002FA7] hover:bg-[#00248a] rounded-sm h-12 px-6 text-sm font-medium"
                data-testid="hero-cta-primary">
                Jetzt kostenlos testen <ArrowRight size={16} className="ml-2" weight="bold" />
              </Button>
              <a href="#features"
                className="inline-flex items-center justify-center h-12 px-6 rounded-sm border border-zinc-300 hover:border-zinc-900 text-sm font-medium text-zinc-900 transition-colors"
                data-testid="hero-cta-secondary">
                Funktionen ansehen
              </a>
            </div>
            <div className="mt-6 flex items-center gap-4 text-xs text-zinc-500">
              <span className="inline-flex items-center gap-1"><CheckCircle size={14} weight="fill" className="text-emerald-600" /> Keine Kreditkarte nötig</span>
              <span className="inline-flex items-center gap-1"><CheckCircle size={14} weight="fill" className="text-emerald-600" /> DSGVO-konform</span>
              <span className="inline-flex items-center gap-1"><CheckCircle size={14} weight="fill" className="text-emerald-600" /> Cloud-Hosting</span>
            </div>
          </div>

          {/* Mock screenshot */}
          <div className="relative">
            <div className="absolute -inset-3 bg-gradient-to-br from-[#002FA7]/15 to-emerald-300/10 rounded-md blur-2xl" />
            <div className="relative bg-white border border-zinc-200 rounded-sm shadow-2xl overflow-hidden">
              <div className="bg-zinc-50 border-b border-zinc-200 px-3 py-2 flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                <div className="ml-2 text-[10px] text-zinc-500 font-mono-num">nutri-calc.org/rezepte/bratensoße</div>
              </div>
              <div className="p-5 sm:p-6 space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">Live-Berechnung</div>
                    <div className="font-heading text-xl font-bold tracking-tight">Bratensoße Klassik</div>
                  </div>
                  <div className="w-14 h-14 rounded-sm bg-emerald-600 text-white flex items-center justify-center font-heading font-extrabold text-3xl shadow-md" title="Nutri-Score">A</div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  {[
                    { l: "Brennwert", v: "127", u: "kcal" },
                    { l: "Fett",     v: "5,2", u: "g" },
                    { l: "Eiweiß",   v: "8,4", u: "g" },
                  ].map((m) => (
                    <div key={m.l} className="bg-zinc-50 border border-zinc-200 rounded-sm p-2.5">
                      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{m.l}</div>
                      <div className="font-mono-num text-lg font-bold leading-tight">{m.v}<span className="text-xs text-zinc-500 font-normal ml-0.5">{m.u}</span></div>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Zutaten</div>
                  <div className="space-y-1 text-xs">
                    {[
                      ["Rindfleisch mager", "0,400 kg"],
                      ["Zwiebel",            "0,150 kg"],
                      ["Rotwein trocken",    "0,200 l"],
                      ["Tomatenmark 28%",    "0,050 kg"],
                    ].map(([n, q]) => (
                      <div key={n} className="flex justify-between border-b border-zinc-100 py-1">
                        <span>{n}</span>
                        <span className="font-mono-num text-zinc-600">{q}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {["Sulfite", "—"].map((a, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 bg-amber-100 text-amber-900 rounded-sm font-medium">{a}</span>
                  ))}
                  <span className="text-[10px] px-2 py-0.5 bg-zinc-100 text-zinc-700 rounded-sm font-medium ml-auto">v3 · zuletzt geändert von Vahit</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ----- Target audience strip ----- */}
      <section className="border-y border-zinc-200 py-6 bg-zinc-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-[11px] uppercase tracking-widest text-zinc-500 mb-4">Entwickelt für</div>
          <div className="flex flex-wrap justify-center gap-x-10 gap-y-3">
            {TARGET_AUDIENCE.map(({ icon: Icon, label }) => (
              <div key={label} className="inline-flex items-center gap-2 text-sm text-zinc-700">
                <Icon size={18} weight="duotone" className="text-[#002FA7]" />
                {label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ----- Features grid ----- */}
      <section id="features" className="py-20 sm:py-28 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-12">
          <div className="text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-2">Funktionen</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
            Alles, was du für eine vollständige Nährwert-Deklaration brauchst.
          </h2>
          <p className="mt-3 text-zinc-600">
            Keine Excel-Frickelei mehr. NutriCalc rechnet alles automatisch — präzise,
            transparent und nachvollziehbar.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map(({ icon: Icon, title, desc }) => (
            <div key={title}
              className="border border-zinc-200 rounded-sm p-5 hover:border-[#002FA7] hover:shadow-md transition-all bg-white"
              data-testid={`feature-${title.toLowerCase().replace(/\s+/g, "-").substring(0, 20)}`}>
              <div className="w-10 h-10 rounded-sm bg-[#002FA7]/10 flex items-center justify-center mb-3">
                <Icon size={20} weight="duotone" className="text-[#002FA7]" />
              </div>
              <h3 className="font-heading font-bold text-base mb-1.5 tracking-tight">{title}</h3>
              <p className="text-sm text-zinc-600 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ----- How it works ----- */}
      <section id="how" className="py-20 sm:py-28 bg-zinc-50 border-y border-zinc-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <div className="text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-2">So funktioniert's</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
              In 3 Schritten zur fertigen Deklaration.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {HOW_IT_WORKS.map(({ n, title, desc }) => (
              <div key={n} className="relative bg-white border border-zinc-200 rounded-sm p-6">
                <div className="font-mono-num text-5xl font-extrabold text-[#002FA7]/15 absolute right-4 top-3 leading-none">{n}</div>
                <h3 className="font-heading font-bold text-lg mb-2 tracking-tight">{title}</h3>
                <p className="text-sm text-zinc-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ----- Compliance / Trust ----- */}
      <section id="compliance" className="py-20 sm:py-28 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-2">Compliance & Sicherheit</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">
              Transparent. Nachvollziehbar. DSGVO-konform.
            </h2>
            <p className="mt-3 text-zinc-600 leading-relaxed">
              Wir wissen, wie streng die Anforderungen der EU-Lebensmittelbranche sind.
              Deshalb ist NutriCalc von Anfang an auf saubere Dokumentation und
              Datenschutz ausgelegt.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                ["EU-Verordnung 1169/2011", "Vollständige Nährwert-Deklaration inkl. 14 Pflichtallergene als Berechnungs- und Arbeitsgrundlage. Die finale rechtliche Prüfung obliegt dem Nutzer."],
                ["DSGVO-konform",            "Pseudonymisierung, Audit-Trail, Recht auf Auskunft & Löschung. Auftragsverarbeiter ist Emergent (Hosting); konkreter Server-Standort wird im DPA verbindlich vereinbart."],
                ["Multi-Tenant-Isolation", "Jede Firma bekommt einen eigenen Datenraum. Kein Zugriff auf fremde Rezepte oder Mitarbeiter."],
                ["Versionierung & Audit-Trail", "Jede Änderung wird mit Zeitstempel und Benutzer protokolliert. Vollständig nachvollziehbar bis zur letzten Zeile."],
              ].map(([title, desc]) => (
                <li key={title} className="flex gap-3">
                  <CheckCircle size={20} weight="fill" className="text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-sm">{title}</div>
                    <div className="text-sm text-zinc-600">{desc}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <div className="absolute -inset-3 bg-gradient-to-br from-emerald-200/30 to-[#002FA7]/15 rounded-md blur-2xl" />
            <div className="relative overflow-hidden rounded-sm border border-zinc-200">
              <img src={FEATURE_KITCHEN} alt="" className="w-full h-80 sm:h-96 object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-900/60 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-white/15 backdrop-blur-md rounded-sm text-[10px] uppercase tracking-widest mb-2">
                  <Lock size={12} /> Bank-Level Sicherheit
                </div>
                <div className="font-heading text-xl font-bold tracking-tight">
                  Deine Rezepte. Dein Wettbewerbsvorteil. Geschützt.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ----- Pricing ----- */}
      <section id="pricing" className="py-20 sm:py-28 bg-zinc-50 border-y border-zinc-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-2">Preise</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mb-3">
            Einfach. Fair. Transparent.
          </h2>
          <p className="text-zinc-600 max-w-2xl mx-auto">
            Ein Preis. Alle Funktionen. Keine versteckten Kosten.
          </p>

          <div className="mt-10 grid md:grid-cols-2 gap-5 text-left">
            {/* Free / Trial */}
            <div className="bg-white border border-zinc-200 rounded-sm p-7">
              <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Testen</div>
              <div className="font-heading text-2xl font-bold tracking-tight">Kostenlose Demo</div>
              <p className="text-sm text-zinc-600 mt-1.5">Unverbindlich ausprobieren — alle Funktionen.</p>
              <div className="my-5 font-heading text-4xl font-extrabold tracking-tight">
                0&nbsp;€ <span className="text-base font-normal text-zinc-500">/ 7 Tage</span>
              </div>
              <ul className="space-y-2 text-sm text-zinc-700 mb-6">
                <li className="flex gap-2"><CheckCircle size={16} weight="fill" className="text-emerald-600 mt-0.5" /> Voller Funktionsumfang</li>
                <li className="flex gap-2"><CheckCircle size={16} weight="fill" className="text-emerald-600 mt-0.5" /> Bis zu 3 Test-Nutzer</li>
                <li className="flex gap-2"><CheckCircle size={16} weight="fill" className="text-emerald-600 mt-0.5" /> Keine Kreditkarte</li>
                <li className="flex gap-2"><CheckCircle size={16} weight="fill" className="text-emerald-600 mt-0.5" /> Daten bleiben dir erhalten</li>
              </ul>
              <Button onClick={() => nav("/registrieren")}
                className="w-full bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm"
                data-testid="pricing-trial-cta">
                Jetzt kostenlos testen
              </Button>
            </div>

            {/* Paid plan */}
            <div className="relative bg-white border-2 border-[#002FA7] rounded-sm p-7 shadow-lg">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-[#002FA7] text-white text-[10px] uppercase tracking-widest rounded-full font-bold flex items-center gap-1">
                <Star size={10} weight="fill" /> Beliebt
              </div>
              <div className="text-xs uppercase tracking-widest text-[#002FA7] mb-2 font-bold">Business</div>
              <div className="font-heading text-2xl font-bold tracking-tight">Team-Plan</div>
              <p className="text-sm text-zinc-600 mt-1.5">Für Manufakturen, Restaurants & Hersteller.</p>
              <div className="my-5 font-heading text-4xl font-extrabold tracking-tight">
                149&nbsp;€ <span className="text-base font-normal text-zinc-500">/ Monat</span>
              </div>
              <div className="text-xs text-zinc-500 mb-4 -mt-3">zzgl. MwSt. · Jederzeit kündbar</div>
              <ul className="space-y-2 text-sm text-zinc-700 mb-6">
                <li className="flex gap-2"><CheckCircle size={16} weight="fill" className="text-[#002FA7] mt-0.5" /> <strong>5 Nutzer inklusive</strong></li>
                <li className="flex gap-2"><CheckCircle size={16} weight="fill" className="text-[#002FA7] mt-0.5" /> Unbegrenzte Rezepte & Zutaten</li>
                <li className="flex gap-2"><CheckCircle size={16} weight="fill" className="text-[#002FA7] mt-0.5" /> PDF / Excel / Etiketten-Export</li>
                <li className="flex gap-2"><CheckCircle size={16} weight="fill" className="text-[#002FA7] mt-0.5" /> Eigenes Firmenlogo in Exports</li>
                <li className="flex gap-2"><CheckCircle size={16} weight="fill" className="text-[#002FA7] mt-0.5" /> Audit-Trail & Versionierung</li>
                <li className="flex gap-2"><CheckCircle size={16} weight="fill" className="text-[#002FA7] mt-0.5" /> E-Mail-Support &lt; 24 h</li>
              </ul>
              <Button onClick={() => nav("/registrieren")}
                className="w-full bg-[#002FA7] hover:bg-[#00248a] text-white rounded-sm"
                data-testid="pricing-pro-cta">
                Jetzt starten <ArrowRight size={14} className="ml-1" weight="bold" />
              </Button>
            </div>
          </div>
          <p className="mt-8 text-xs text-zinc-500">
            Brauchst du mehr als 5 Nutzer oder eine individuelle Lösung? <a href="mailto:vahits@icloud.com" className="text-[#002FA7] hover:underline">Sprich uns an</a>.
          </p>
        </div>
      </section>

      {/* ----- Final CTA ----- */}
      <section className="py-24 sm:py-32 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-heading text-3xl sm:text-5xl font-extrabold tracking-tight">
          Bereit für deine erste Deklaration?
        </h2>
        <p className="mt-4 text-zinc-600 text-base sm:text-lg max-w-xl mx-auto">
          7 Tage kostenlos. Kein Risiko. Keine Kreditkarte. Einfach loslegen.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Button onClick={() => nav("/registrieren")}
            className="bg-[#002FA7] hover:bg-[#00248a] rounded-sm h-12 px-7 text-sm font-medium"
            data-testid="final-cta">
            Jetzt kostenlos testen <ArrowRight size={16} className="ml-2" weight="bold" />
          </Button>
        </div>
      </section>

      {/* ----- Footer ----- */}
      <footer className="border-t border-zinc-200 bg-zinc-50 py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row justify-between gap-4 text-xs text-zinc-500">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-sm bg-[#002FA7] flex items-center justify-center">
              <ChartLineUp size={12} weight="bold" color="white" />
            </div>
            <span>© {new Date().getFullYear()} NutriCalc by Vahit Sarpkaya — Alle Rechte vorbehalten</span>
          </div>
          <div className="flex gap-5">
            <Link to="/impressum" className="hover:text-zinc-900" data-testid="footer-imprint">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-zinc-900" data-testid="footer-privacy">Datenschutz</Link>
            <Link to="/agb" className="hover:text-zinc-900" data-testid="footer-terms">AGB</Link>
            <a href="#" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("nc:open-cookie-settings")); }}
              className="hover:text-zinc-900" data-testid="footer-cookies">Cookie-Einstellungen</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
